import { Box, Chip, capitalize, Popover, Stack } from "@mui/material";
import type { GridRenderCellParams } from "@mui/x-data-grid";
import { useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { useState } from "react";
import { useApiMutation } from "../hooks/use-api-mutation";
import { useUpdateFlow } from "../hooks/use-flows";
import type { PersistedUserFlow, Test } from "../types";
import { CHIP_COLOR_OVERRIDES, getChipColors } from "../utils/chip-colors";
import { clickableProps } from "../utils/keyboard";

// TODO: Move to types.ts
interface TestCase {
  id: string;
  name: string;
  type: "flow" | "test";
  status: string;
  priority: string;
  severity: string;
  created_at: string;
  created_by: string;
  source?: string;
  test_id?: string;
  flow_data?: PersistedUserFlow;
  test_data?: Test;
}

const getPriorityColor = (priority: string) => {
  switch (priority) {
    case "urgent":
      return "error";
    case "high":
      return "warning";
    case "medium":
      return "info";
    default:
      return "default";
  }
};

export const EditablePriorityCell = (params: GridRenderCellParams<TestCase>) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [value, setValue] = useState(params.value);
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const open = Boolean(anchorEl);

  const handleClose = () => {
    setAnchorEl(null);
  };

  const { mutate: updateTest } = useApiMutation<{ name: string; priority: string }, void>(
    "PUT",
    "",
    {
      onSuccess: () => {
        queryClient.invalidateQueries();
        enqueueSnackbar("Priority updated successfully", {
          variant: "success",
        });
        handleClose();
      },
      onError: (error) => {
        enqueueSnackbar(`Error updating priority: ${error.message}`, {
          variant: "error",
        });
        setValue(params.value); // Reset to original value
        handleClose();
      },
    },
  );

  const { mutate: updateFlow } = useUpdateFlow(params.row.flow_data?.id || "");

  const handleOptionSelect = (newValue: string) => {
    setValue(newValue);
    if (params.row.type === "test" && params.row.test_data) {
      updateTest({
        _url: `/tests/${params.row.test_data.id}`,
        name: params.row.name, // Keep existing name
        priority: newValue,
      });
    } else if (params.row.type === "flow" && params.row.flow_data) {
      updateFlow({
        priority: newValue as "low" | "medium" | "high" | "urgent",
      });
    }
    handleClose();
  };

  const priorityOptions = [
    { value: "low", label: "Low", color: "default" as const },
    { value: "medium", label: "Medium", color: "info" as const },
    { value: "high", label: "High", color: "warning" as const },
    { value: "urgent", label: "Urgent", color: "error" as const },
  ];

  const priorityColor = getPriorityColor(value);
  return (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          cursor: "pointer",
          width: "100%",
          height: "100%",
          position: "relative",
        }}
        {...clickableProps((event: unknown) => {
          (event as React.MouseEvent).stopPropagation?.();
          setAnchorEl((event as React.MouseEvent).currentTarget as HTMLElement);
        })}
        onClick={(event) => {
          event.stopPropagation();
          setAnchorEl(event.currentTarget);
        }}
      >
        <Chip
          label={capitalize(value)}
          variant="outlined"
          size="small"
          color={
            priorityColor as
              | "default"
              | "primary"
              | "secondary"
              | "error"
              | "info"
              | "success"
              | "warning"
          }
          sx={{
            fontSize: "0.75rem",
            fontWeight: 500,
            height: 24,
            "& .MuiChip-label": {
              px: 1.5,
            },
            borderWidth: 1.5,
            ...CHIP_COLOR_OVERRIDES,
          }}
        />
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        sx={{
          "& .MuiPopover-paper": {
            width: "auto",
            minWidth: 120,
          },
        }}
      >
        <Box sx={{ p: 1 }}>
          <Stack spacing={0.5}>
            {priorityOptions.map((option) => (
              <Chip
                key={option.value}
                label={option.label}
                variant={value === option.value ? "filled" : "outlined"}
                size="small"
                color={option.color}
                onClick={() => handleOptionSelect(option.value)}
                sx={{
                  fontSize: "0.75rem",
                  fontWeight: 500,
                  height: 24,
                  cursor: "pointer",
                  "& .MuiChip-label": {
                    px: 1.5,
                  },
                  borderWidth: 1.5,
                  ...(() => {
                    const colors = getChipColors(option.color);
                    return {
                      backgroundColor: value === option.value ? colors.bg : "transparent",
                      borderColor: colors.border,
                      color: colors.text,
                      "&:hover": { backgroundColor: colors.bg },
                    };
                  })(),
                }}
              />
            ))}
          </Stack>
        </Box>
      </Popover>
    </>
  );
};

export default EditablePriorityCell;
